import ProjectForm, { ProjectFormValues } from '../ProjectForm/ProjectForm';
import { Formik, FormikHelpers } from 'formik';
import { Modal } from '@round/whitelabel-ui';
import { creatorbase, creatorbaseQueries } from '@round/api';
import { showNotification } from 'helpers';
import {
    convertPostProjectFormApiErrorsToFormikErrors,
    projectFormValidationSchema,
} from '../ProjectForm/ProjectForm.helpers';
import { Button } from '@round/whitelabel-ui';
import styles from './CreateProjectModal.module.css';
import { useCreateProjectMutation } from '@round/creatorbase-helpers/api-hooks';

type Props = {
    isOpen: boolean;
    onClose: () => void;
};

const initialValues: ProjectFormValues = {
    name: '',
    brand: null,
    image: null,
};

const CreateProjectModal = ({ isOpen, onClose }: Props) => {
    const { create } = useCreateProjectMutation();

    const handleSubmit = async (values: ProjectFormValues, helpers: FormikHelpers<ProjectFormValues>) => {
        const data: creatorbase.PostProjectData = {
            name: values.name,
            brand_id: values.brand!.value,
            image: values.image ?? undefined,
        };

        try {
            await create(data);
            helpers.resetForm();
            onClose();
        } catch (error) {
            if (
                error instanceof Error &&
                creatorbaseQueries.project.utils.create.isExpectedErrorResponse(error) &&
                error.response.status === 400 &&
                !Array.isArray(error.response.data)
            ) {
                helpers.setErrors(convertPostProjectFormApiErrorsToFormikErrors(error.response.data));
                return;
            }
            showNotification(
                error instanceof Error
                    ? creatorbaseQueries.project.utils.create.getErrorMessage(error)
                    : 'Could not create project',
                'error'
            );
        }
    };

    return (
        <Modal
            className={styles.modal}
            isOpen={isOpen}
            onClose={onClose}
            closeOnOverlayClick
            ariaLabel="create project modal"
        >
            <Modal.Header>
                <Modal.Title>Create project</Modal.Title>
            </Modal.Header>
            <Formik
                validationSchema={projectFormValidationSchema}
                initialValues={initialValues}
                onSubmit={handleSubmit}
            >
                {({ isValid, isSubmitting, submitForm, dirty }) => (
                    <>
                        <Modal.Body>
                            <ProjectForm />
                        </Modal.Body>

                        <Modal.Actions>
                            <Button
                                appearance="primary"
                                onClick={submitForm}
                                disabled={!isValid || isSubmitting || !dirty}
                                isLoading={isSubmitting}
                            >
                                Create project
                            </Button>
                        </Modal.Actions>
                    </>
                )}
            </Formik>
        </Modal>
    );
};

export default CreateProjectModal;
